/**
 * CallbacksCard — scheduled call times for one contact, on the detail right
 * rail. Three jobs:
 *   1. Show the upcoming booked callback prominently (with complete / no-show /
 *      cancel one-tap outcomes) and past callbacks behind a toggle.
 *   2. Book one as a recruiter ("they asked me to call Tuesday at 3") via an
 *      inline form with quick picks — any future time, unlike the patient grid.
 *   3. Send the patient a "pick a time" booking link by SMS (the Trialfacts
 *      flow): POST /contacts/:id/booking-link { send:true } — same compliance
 *      gate + test-mode override as the composer.
 * Also surfaces the contact's cadence state ("Attempt 2 of 6 · next follow-up
 * due in 3h") so a recruiter on the detail page sees where outreach stands.
 */
import { FormEvent, useCallback, useEffect, useState } from 'react';
import { CalendarClock, Check, Link2, Loader2, PhoneMissed, Plus, X } from 'lucide-react';
import api from '../../services/api';
import { Contact, ScheduledCallback } from '../types';
import { notifyStatsChanged, relTime } from '../lib';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

/** A Date as a datetime-local input value (local clock, minute precision). */
function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function quickSlot(days: number, hour: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  d.setHours(hour, 0, 0, 0);
  return toLocalInput(d);
}

const QUICK_SLOTS: Array<{ label: string; value: () => string }> = [
  { label: 'In 2 hours', value: () => { const d = new Date(Date.now() + 2 * 3600e3); d.setMinutes(0, 0, 0); return toLocalInput(d); } },
  { label: 'Tomorrow 9am', value: () => quickSlot(1, 9) },
  { label: 'Tomorrow 3pm', value: () => quickSlot(1, 15) },
];

/** "Mon, Jun 15 at 3:00 PM" in the viewer's locale. */
function slotLabel(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })} at ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
}

const isBooked = (cb: ScheduledCallback) => cb.status === 'booked';

interface Props {
  contact: Contact;
  /** Tenant cadence ceiling (GET /contacts/:id max_attempts; defaults to 6). */
  maxAttempts?: number;
  /** Bump to refetch (e.g. after the timeline reloads). */
  reloadToken?: number;
  /** Fired after the card itself sends an SMS (booking link) so the parent can
   * reload the timeline + contact cadence state. */
  onSent?: () => void;
  /** Drop the rendered "pick a time" SMS into the docked composer (editable). */
  prefillComposer?: (text: string) => void;
}

export default function CallbacksCard({ contact, maxAttempts = 6, reloadToken = 0, prefillComposer }: Props) {
  const contactId = contact.id;
  const [callbacks, setCallbacks] = useState<ScheduledCallback[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [showPast, setShowPast] = useState(false);

  // Inline book form
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [when, setWhen] = useState('');
  const [note, setNote] = useState('');

  // Booking-link send
  const [sendingLink, setSendingLink] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/api/crm/contacts/${contactId}/callbacks`);
      setCallbacks(res.data?.callbacks || []);
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => { load(); }, [load, reloadToken]);

  const setBusy = (id: string, busy: boolean) =>
    setBusyIds((prev) => {
      const n = new Set(prev);
      if (busy) n.add(id); else n.delete(id);
      return n;
    });

  const setStatus = async (cb: ScheduledCallback, status: string) => {
    if (busyIds.has(cb.id)) return;
    setActionError(null);
    setBusy(cb.id, true);
    setCallbacks((prev) => prev.map((c) => (c.id === cb.id ? { ...c, status } : c)));
    try {
      await api.patch(`/api/crm/callbacks/${cb.id}`, { status });
      notifyStatsChanged(); // worklist buckets + the sidebar Today badge moved
    } catch (e: any) {
      setCallbacks((prev) => prev.map((c) => (c.id === cb.id ? { ...c, status: cb.status } : c)));
      setActionError(errMsg(e));
    } finally {
      setBusy(cb.id, false);
    }
  };

  const book = async (e: FormEvent) => {
    e.preventDefault();
    if (!when || saving) return;
    setSaving(true);
    setActionError(null);
    try {
      const res = await api.post(`/api/crm/contacts/${contactId}/callbacks`, {
        scheduled_at: new Date(when).toISOString(),
        note: note.trim() || null,
      });
      const cb: ScheduledCallback | undefined = res.data?.callback;
      if (cb) setCallbacks((prev) => [cb, ...prev].sort((a, b) => b.scheduled_at.localeCompare(a.scheduled_at)));
      notifyStatsChanged(); // a booked callback reshuffles the worklist buckets
      setWhen('');
      setNote('');
      setFormOpen(false);
    } catch (e2: any) {
      setActionError(errMsg(e2));
    } finally {
      setSaving(false);
    }
  };

  // Draft the rendered "pick a time" SMS into the docked composer instead of
  // direct-sending: the recruiter edits the wording and sends via the normal
  // gated /messages path (opt-out / consent / quiet-hours / char-limit + the
  // outreach-attempt accounting all happen server-side on that send). Minting
  // on { send:false } reuses the live token, so the previewed URL ships as-is.
  const sendLink = async () => {
    if (sendingLink) return;
    setSendingLink(true);
    setActionError(null);
    setNotice(null);
    try {
      const res = await api.post(`/api/crm/contacts/${contactId}/booking-link`, { send: false });
      const body: string | undefined = res.data?.body;
      if (body && prefillComposer) {
        prefillComposer(body);
        setNotice('Draft added to the message box below — edit and send from there.');
      } else {
        setNotice(res.data?.url ? 'Booking link ready.' : 'Booking link created.');
      }
    } catch (e: any) {
      const hint = e?.response?.data?.hint;
      setActionError(hint ? `${errMsg(e)} — ${hint}` : errMsg(e));
    } finally {
      setSendingLink(false);
    }
  };

  const now = Date.now();
  const upcoming = callbacks
    .filter((cb) => isBooked(cb))
    .sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at));
  const past = callbacks.filter((cb) => !isBooked(cb));

  // Cadence status line from the trigger-maintained contact columns. Numbering
  // matches the Today page: "Attempt N of M" names the attempt that is DUE
  // (attempt_count = attempts already made). With a booked callback, the
  // cadence is paused (next_action_at points at the slot), so say that instead.
  const attempts = contact.attempt_count ?? 0;
  const dueLabelText = contact.next_action_at
    ? (new Date(contact.next_action_at).getTime() <= now ? 'due now' : relTime(contact.next_action_at))
    : '';
  const cadenceLine = contact.needs_reply_at
    ? `Waiting on your reply (${relTime(contact.needs_reply_at)})`
    : upcoming.length > 0
      ? 'Outreach cadence paused — callback booked'
      : contact.next_action_at
        ? attempts === 0
          ? `First touch ${dueLabelText}`
          : `Attempt ${Math.min(attempts + 1, maxAttempts)} of ${maxAttempts} · next follow-up ${dueLabelText}`
        : attempts > 0
          ? `${attempts} contact attempt${attempts === 1 ? '' : 's'} made`
          : null;

  const inputCls =
    'w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400';

  const statusChip = (cb: ScheduledCallback) => {
    const map: Record<string, string> = {
      completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
      cancelled: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
      no_show: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    };
    return map[cb.status] || 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="inline-flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
          <CalendarClock className="h-4 w-4 text-primary-600 dark:text-primary-400" />
          Callbacks
          {!loading && upcoming.length > 0 && (
            <span className="text-sm font-normal text-gray-500 dark:text-gray-400">({upcoming.length})</span>
          )}
        </h3>
        <button
          onClick={() => { setFormOpen((s) => !s); setActionError(null); }}
          aria-expanded={formOpen}
          className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-primary-600 px-3 py-1.5 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400 dark:hover:bg-primary-900/20 sm:min-h-0"
        >
          {formOpen ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
          {formOpen ? 'Cancel' : 'Schedule'}
        </button>
      </div>

      {cadenceLine && (
        <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">{cadenceLine}</p>
      )}

      {formOpen && (
        <form onSubmit={book} className="mb-3 space-y-2.5 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40">
          <div>
            <input
              type="datetime-local"
              value={when}
              onChange={(e) => setWhen(e.target.value)}
              aria-label="Callback date and time"
              autoFocus
              className={inputCls}
            />
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {QUICK_SLOTS.map((q) => (
                <button
                  key={q.label}
                  type="button"
                  onClick={() => setWhen(q.value())}
                  className="rounded-full border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  {q.label}
                </button>
              ))}
            </div>
          </div>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Note (optional) — e.g. prefers afternoons"
            aria-label="Callback note"
            maxLength={500}
            className={inputCls}
          />
          <button
            type="submit"
            disabled={!when || saving}
            className="inline-flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:focus:ring-offset-gray-900 sm:min-h-0"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />}
            {saving ? 'Booking…' : 'Book callback'}
          </button>
        </form>
      )}

      {notice && (
        <div className="mb-3 rounded-lg border border-primary-200 bg-primary-50 p-2.5 text-xs text-primary-800 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-300">
          {notice}
        </div>
      )}
      {actionError && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-2.5 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {actionError}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-4 w-4 animate-spin text-primary-600" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {error}
          <button onClick={load} className="ml-2 font-medium underline hover:no-underline">Retry</button>
        </div>
      ) : (
        <>
          {upcoming.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No callback scheduled. Book one, or text the patient a link to pick their own time.
            </p>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {upcoming.map((cb) => {
                const busy = busyIds.has(cb.id);
                const overdue = new Date(cb.scheduled_at).getTime() < now;
                return (
                  <div key={cb.id} className="flex items-start gap-2.5 py-2.5">
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-medium ${overdue ? 'text-red-700 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'}`}>
                        {slotLabel(cb.scheduled_at)}
                        {overdue && <span className="ml-1.5 text-xs font-normal">(overdue)</span>}
                      </p>
                      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                        {cb.booked_via === 'public_link' ? 'Patient-booked' : 'Recruiter-booked'} · {relTime(cb.scheduled_at)}
                        {cb.note ? ` · ${cb.note}` : ''}
                      </p>
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-1">
                      <button
                        onClick={() => setStatus(cb, 'completed')}
                        disabled={busy}
                        title="Mark completed"
                        aria-label="Mark callback completed"
                        className="flex h-11 w-11 items-center justify-center rounded-full sm:h-8 sm:w-8 border border-gray-300 text-gray-400 transition-colors duration-150 hover:border-green-600 hover:text-green-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-wait disabled:opacity-50 dark:border-gray-600"
                      >
                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                      </button>
                      <button
                        onClick={() => setStatus(cb, 'no_show')}
                        disabled={busy}
                        title="No-show"
                        aria-label="Mark callback as no-show"
                        className="flex h-11 w-11 items-center justify-center rounded-full sm:h-8 sm:w-8 border border-gray-300 text-gray-400 transition-colors duration-150 hover:border-red-600 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-wait disabled:opacity-50 dark:border-gray-600"
                      >
                        <PhoneMissed className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => setStatus(cb, 'cancelled')}
                        disabled={busy}
                        title="Cancel callback"
                        aria-label="Cancel callback"
                        className="flex h-11 w-11 items-center justify-center rounded-full sm:h-8 sm:w-8 border border-gray-300 text-gray-400 transition-colors duration-150 hover:border-gray-500 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-wait disabled:opacity-50 dark:border-gray-600 dark:hover:text-gray-300"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <button
            onClick={sendLink}
            disabled={sendingLink || !contact.phone_e164 || contact.sms_opt_out}
            title={
              contact.sms_opt_out
                ? 'Patient opted out of SMS'
                : !contact.phone_e164
                  ? 'No phone number on file'
                  : 'Text the patient a link to pick their own call time'
            }
            className="mt-3 inline-flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-lg border border-primary-600 px-3 py-2 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:text-primary-400 dark:hover:bg-primary-900/20 sm:min-h-0"
          >
            {sendingLink ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />}
            {sendingLink ? 'Drafting…' : 'Draft "pick a time" text'}
          </button>

          {past.length > 0 && (
            <div className="mt-2 border-t border-gray-100 pt-2 dark:border-gray-700">
              <button
                onClick={() => setShowPast((s) => !s)}
                aria-expanded={showPast}
                className="text-xs font-medium text-gray-500 transition-colors duration-150 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:text-gray-200"
              >
                {showPast ? 'Hide' : 'Show'} past ({past.length})
              </button>
              {showPast && (
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                  {past.map((cb) => (
                    <div key={cb.id} className="flex items-center gap-2.5 py-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-gray-500 dark:text-gray-400">{slotLabel(cb.scheduled_at)}</p>
                      </div>
                      <span className={`inline-flex flex-shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusChip(cb)}`}>
                        {cb.status === 'no_show' ? 'no-show' : cb.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
